import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const checklistItems = [
  { id: 1, category: 'Essentials', text: 'Passport & Travel Visas' },
  { id: 2, category: 'Essentials', text: 'International Power Grid Adapter' },
  { id: 3, category: 'Apparel', text: 'Merino Wool Base Layers (x3)' },
  { id: 4, category: 'Apparel', text: 'Packable Weather Shell' },
  { id: 5, category: 'Toiletries', text: 'Solid Cologne/Hygiene Core' },
  { id: 6, category: 'Toiletries', text: 'TSA-Compliant Liquid Pouches' },
  { id: 7, category: 'Tech', text: '20,000mAh Rapid Charge Bank' },
  { id: 8, category: 'Tech', text: 'Active Noise-Canceling Core' },
];

const Checklist = () => {
  const [checked, setChecked] = useState([1, 4, 7]);

  const toggleCheck = (id) => {
    setChecked(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  return (
    <section id="system" className="py-32 bg-parchment-dark/20 relative border-y border-cobalt/10 bg-topo-pattern overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-24 items-center">
        
        <motion.div 
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="space-y-10"
        >
          <div className="stamp-badge">Field Methodology</div>
          <h2 className="text-5xl md:text-6xl font-bold text-cobalt leading-tight">
            The Art of <br />
            <span className="italic font-serif text-amber">Subtraction.</span>
          </h2>
          <p className="text-xl text-ink-light leading-relaxed font-light">
            Packing is an exercise in ruthless prioritization. Our systems are designed to highlight what you need and eliminate what you don't.
          </p>
          
          <div className="flex items-center gap-6 p-6 journal-border bg-white/40 max-w-md group cursor-pointer hover:bg-white/80 transition-colors">
            <div className="w-12 h-12 bg-cobalt text-white rounded-full flex items-center justify-center group-hover:bg-amber transition-colors">
              <SafeIcon icon={FiIcons.FiDownload} className="text-xl" />
            </div>
            <div>
              <p className="font-bold uppercase tracking-widest text-xs text-cobalt">Full Manifest</p>
              <p className="text-sm font-serif italic text-ink-light">Download the Complete Deployment Guide (PDF)</p>
            </div>
          </div>
        </motion.div>

        {/* Interactive Journal Paper Checklist */}
        <motion.div 
          initial={{ opacity: 0, rotate: 3, y: 30 }}
          whileInView={{ opacity: 1, rotate: -1, y: 0 }}
          viewport={{ once: true }}
          className="bg-[#FDFBF7] p-8 md:p-12 shadow-2xl relative"
          style={{
            boxShadow: '20px 20px 40px rgba(0,0,0,0.08)',
            borderLeft: '8px solid #1E3A8A'
          }}
        >
          {/* Lined paper effect */}
          <div className="absolute inset-0 pointer-events-none opacity-40" 
               style={{ backgroundImage: 'linear-gradient(transparent 95%, #e5e7eb 5%)', backgroundSize: '100% 2.5rem' }}>
          </div>

          <div className="relative z-10">
            <div className="flex justify-between items-start mb-10 border-b-2 border-ink pb-6">
              <div>
                <h3 className="font-serif text-3xl text-cobalt">Standard Deployment</h3>
                <p className="text-[10px] font-bold uppercase tracking-[0.4em] text-amber mt-2">Protocol Vol. IV / 2024</p>
              </div>
              <div className="text-right hidden sm:block">
                <span className="text-xs font-mono text-ink-light/40">REF: NC-882</span>
              </div>
            </div>
            
            <div className="space-y-1">
              {checklistItems.map((item) => {
                const isChecked = checked.includes(item.id);
                return (
                  <div 
                    key={item.id} 
                    className="flex items-center gap-6 py-3 cursor-pointer group hover:bg-amber/5 px-2 -mx-2 transition-colors h-10"
                    onClick={() => toggleCheck(item.id)}
                  >
                    <div className={`w-5 h-5 border-2 flex items-center justify-center transition-all duration-300 ${
                      isChecked ? 'border-amber bg-amber scale-110' : 'border-cobalt/30 group-hover:border-cobalt'
                    }`}>
                      {isChecked && <SafeIcon icon={FiIcons.FiCheck} className="text-white text-[10px]" />}
                    </div>
                    <span className={`text-lg transition-all duration-500 font-medium ${
                      isChecked ? 'text-ink-light/30 line-through' : 'text-ink'
                    }`}>
                      {item.text}
                    </span>
                    <span className="ml-auto text-[10px] font-black uppercase tracking-widest text-ink-light/30 hidden sm:block">
                      {item.category}
                    </span>
                  </div>
                );
              })}
            </div>
            
            <div className="mt-12 pt-6 border-t border-dashed border-gray-300 flex justify-between items-center">
              <span className="font-serif italic text-sm text-ink-light">
                Readiness Score: {Math.round((checked.length / checklistItems.length) * 100)}%
              </span>
              <div className="flex gap-2">
                <div className="w-2 h-2 rounded-full bg-amber animate-pulse"></div>
                <div className="w-2 h-2 rounded-full bg-cobalt/20"></div>
                <div className="w-2 h-2 rounded-full bg-cobalt/20"></div>
              </div>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default Checklist;